import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Calendar,
  Users,
  DollarSign,
  Search,
  Filter,
  Plus,
  Save,
  X,
  UserPlus,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import ExtrasModal from "../components/ExtrasModal";
import EmployeeModal from "../components/EmployeeModal";
import "../styles/CompanySalaries.css";

const BothSalaries = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlMonth = searchParams.get("month");
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customers, setCustomers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editingToPay, setEditingToPay] = useState({});
  const [editingHours, setEditingHours] = useState({}); // Track contractor/company hours editing
  const [editingPayKablan, setEditingPayKablan] = useState({}); // Track קבלן pay editing
  const [editingPayCompany, setEditingPayCompany] = useState({}); // Track חברה pay editing
  const [extrasModalOpen, setExtrasModalOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newSalaryForm, setNewSalaryForm] = useState({
    employee_id: "",
    regular_hours: "",
    weekend_hours: "",
    salary_amount: "",
  });

  useEffect(() => {
    // If URL month is provided, use it directly without fetching available months
    if (urlMonth) {
      setSelectedMonth(urlMonth);
    } else {
      loadAvailableMonths();
    }
    loadBothEmployees();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadSalaries();
      loadSummary();
      // Load available months in background for the dropdown (only if not already loaded)
      if (availableMonths.length === 0) {
        loadAvailableMonthsForDropdown();
      }
    }
  }, [selectedMonth]);

  const loadAvailableMonthsForDropdown = async () => {
    try {
      const [dashboardResponse, presenceResponse] = await Promise.all([
        apiService
          .getAvailableMonths()
          .catch(() => ({ success: false, data: [] })),
        apiService
          .getPresenceAvailableMonths()
          .catch(() => ({ success: false, months: [] })),
      ]);
      const dashboardMonths = dashboardResponse.data || [];
      const presenceMonths = presenceResponse.months || [];
      const allMonths = [
        ...new Set([...dashboardMonths, ...presenceMonths]),
      ].sort((a, b) => b.localeCompare(a));
      if (allMonths.length > 0) {
        setAvailableMonths(allMonths);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
    }
  };

  const loadAvailableMonths = async () => {
    try {
      const [dashboardResponse, presenceResponse] = await Promise.all([
        apiService
          .getAvailableMonths()
          .catch(() => ({ success: false, data: [] })),
        apiService
          .getPresenceAvailableMonths()
          .catch(() => ({ success: false, months: [] })),
      ]);
      const dashboardMonths = dashboardResponse.data || [];
      const presenceMonths = presenceResponse.months || [];
      const allMonths = [
        ...new Set([...dashboardMonths, ...presenceMonths]),
      ].sort((a, b) => b.localeCompare(a));
      if (allMonths.length > 0) {
        setAvailableMonths(allMonths);
        setSelectedMonth(allMonths[0]);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
    }
  };

  const loadBothEmployees = async () => {
    try {
      const response = await apiService.getEmployees({ limit: 10000 });
      const employeesList = response.employees || response.data || [];
      setEmployees(employeesList);
    } catch (error) {
      console.error("Failed to load employees:", error);
    }
  };

  const loadSalaries = async () => {
    try {
      setLoading(true);

      // Fetch from both company_salaries AND salaries tables
      // קבלן ו חברה employees might have records in either table
      const [companySalariesResponse, salariesResponse] = await Promise.all([
        apiService.getCompanySalaries(selectedMonth, user?.partnership_id || 1),
        apiService.getSalaries(selectedMonth, user?.partnership_id || 1),
      ]);

      const companySalariesData = companySalariesResponse.success
        ? companySalariesResponse.data || []
        : [];
      const salariesData = salariesResponse.success
        ? salariesResponse.data || []
        : [];

      // Combine and deduplicate by employee_id (prefer company_salaries if both exist)
      const employeeMap = new Map();

      // Add salaries first (from salaries table - קבלן records)
      for (const salary of salariesData) {
        if (salary.employee_type === "קבלן ו חברה") {
          employeeMap.set(salary.employee_id, salary);
        }
      }

      // Add/override with company_salaries (company_salaries table)
      for (const salary of companySalariesData) {
        if (salary.employee_type === "קבלן ו חברה") {
          employeeMap.set(salary.employee_id, salary);
        }
      }

      // Convert back to array and sort
      const bothSalaries = Array.from(employeeMap.values()).sort((a, b) =>
        (a.employee_name || "").localeCompare(b.employee_name || "", "he"),
      );

      setSalaries(bothSalaries);

      // Extract unique customers for filter dropdown
      const uniqueCustomers = [
        ...new Set(bothSalaries.map((s) => s.customer_name).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b, "he"));

      setCustomers(uniqueCustomers);
    } catch (error) {
      console.error("Failed to load salaries:", error);
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await apiService.getCompanySalarySummary(
        selectedMonth,
        user?.partnership_id || 1,
      );
      if (response.success) {
        setSummary(response);
      }
    } catch (error) {
      console.error("Failed to load summary:", error);
    }
  };

  const handleUpdateToPay = async (salary) => {
    const newValue = editingToPay[salary.id];

    if (!newValue || isNaN(newValue)) {
      alert("אנא הזן סכום תקין");
      return;
    }

    const confirmed = window.confirm(
      `האם אתה בטוח שברצונך לשנות את סה"כ לתשלום עבור ${
        salary.employee_name
      }?\n\nמ-₪${parseFloat(salary.to_pay).toFixed(2)} ל-₪${parseFloat(
        newValue,
      ).toFixed(2)}`,
    );

    if (!confirmed) {
      setEditingToPay((prev) => {
        const updated = { ...prev };
        delete updated[salary.id];
        return updated;
      });
      return;
    }

    try {
      const data = { to_pay: parseFloat(newValue) };
      const response = await apiService.updateCompanySalary(salary.id, data);

      if (response.success) {
        setSalaries((prevSalaries) =>
          prevSalaries.map((s) =>
            s.id === salary.id ? { ...s, to_pay: parseFloat(newValue) } : s,
          ),
        );
        setEditingToPay((prev) => {
          const updated = { ...prev };
          delete updated[salary.id];
          return updated;
        });
        await loadSummary();
        alert('✅ סה"כ לתשלום עודכן בהצלחה');
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update to_pay:", error);
      alert('שגיאה בעדכון סה"כ לתשלום');
    }
  };

  const handleUpdateSalary = async (salaryId, field, value) => {
    try {
      const data = { [field]: value };
      const response = await apiService.updateCompanySalary(salaryId, data);

      if (response.success) {
        setSalaries((prevSalaries) =>
          prevSalaries.map((salary) =>
            salary.id === salaryId ? { ...salary, [field]: value } : salary,
          ),
        );

        if (field === "paid" || field === "extras") {
          await loadSummary();
        }
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update salary:", error);
      alert("שגיאה בעדכון משכורת");
    }
  };

  // Handle contractor hours change - auto-calculate company hours
  const handleContractorHoursChange = (salary, newContractorHours) => {
    const totalHours =
      (salary.total_regular_hours || 0) + (salary.total_weekend_hours || 0);
    const contractorHours = parseFloat(newContractorHours) || 0;
    const companyHours = Math.max(0, totalHours - contractorHours);

    setEditingHours((prev) => ({
      ...prev,
      [salary.id]: {
        contractor: contractorHours,
        company: companyHours,
        total: totalHours,
      },
    }));
  };

  // Handle company hours change - auto-calculate contractor hours
  const handleCompanyHoursChange = (salary, newCompanyHours) => {
    const totalHours =
      (salary.total_regular_hours || 0) + (salary.total_weekend_hours || 0);
    const companyHours = parseFloat(newCompanyHours) || 0;
    const contractorHours = Math.max(0, totalHours - companyHours);

    setEditingHours((prev) => ({
      ...prev,
      [salary.id]: {
        contractor: contractorHours,
        company: companyHours,
        total: totalHours,
      },
    }));
  };

  // Save hours split
  const handleSaveHoursSplit = async (salary) => {
    const hours = editingHours[salary.id];
    if (!hours) return;

    try {
      // Calculate payments based on hours and rates
      const kablanPay = hours.contractor * (salary.rate || 0);
      const companyPay = hours.company * (salary.rate || 0);
      const totalToPay = kablanPay + companyPay;

      const data = {
        contractor_hours: hours.contractor,
        company_hours: hours.company,
        kablan_pay: kablanPay,
        company_pay: companyPay,
        to_pay: totalToPay,
      };

      const response = await apiService.updateCompanySalary(salary.id, data);

      if (response.success) {
        setSalaries((prev) =>
          prev.map((s) =>
            s.id === salary.id
              ? {
                  ...s,
                  contractor_hours: hours.contractor,
                  company_hours: hours.company,
                  kablan_pay: kablanPay,
                  company_pay: companyPay,
                  to_pay: totalToPay,
                }
              : s,
          ),
        );
        setEditingHours((prev) => {
          const updated = { ...prev };
          delete updated[salary.id];
          return updated;
        });
        await loadSummary();
      }
    } catch (error) {
      console.error("Failed to save hours split:", error);
      alert("שגיאה בשמירת חלוקת שעות");
    }
  };

  // Handle קבלן payment update
  const handleUpdateKablanPay = async (salary) => {
    const newValue = editingPayKablan[salary.id];
    if (newValue === undefined) return;

    try {
      const data = { kablan_pay: parseFloat(newValue) || 0 };
      const response = await apiService.updateCompanySalary(salary.id, data);

      if (response.success) {
        setSalaries((prev) =>
          prev.map((s) =>
            s.id === salary.id
              ? { ...s, kablan_pay: parseFloat(newValue) || 0 }
              : s,
          ),
        );
        setEditingPayKablan((prev) => {
          const updated = { ...prev };
          delete updated[salary.id];
          return updated;
        });
        await loadSummary();
      }
    } catch (error) {
      console.error("Failed to update kablan pay:", error);
    }
  };

  // Handle חברה payment update
  const handleUpdateCompanyPay = async (salary) => {
    const newValue = editingPayCompany[salary.id];
    if (newValue === undefined) return;

    try {
      const data = { company_pay: parseFloat(newValue) || 0 };
      const response = await apiService.updateCompanySalary(salary.id, data);

      if (response.success) {
        setSalaries((prev) =>
          prev.map((s) =>
            s.id === salary.id
              ? { ...s, company_pay: parseFloat(newValue) || 0 }
              : s,
          ),
        );
        setEditingPayCompany((prev) => {
          const updated = { ...prev };
          delete updated[salary.id];
          return updated;
        });
        await loadSummary();
      }
    } catch (error) {
      console.error("Failed to update company pay:", error);
    }
  };

  // Open employee edit modal
  const openEmployeeModal = async (salary) => {
    try {
      const response = await apiService.getEmployee(salary.employee_id);
      const employee = response.employee || response.data || response;
      setSelectedEmployee(employee);
    } catch (error) {
      console.error("Failed to load employee:", error);
      setSelectedEmployee({
        employee_id: salary.employee_id,
        employee_name: salary.employee_name,
      });
    }
    setEmployeeModalOpen(true);
  };

  const startEdit = (salary) => {
    setEditingId(salary.id);
    setEditForm({
      regular_hours: salary.regular_hours || 0,
      weekend_hours: salary.weekend_hours || 0,
      hour_rate: salary.hour_rate || 0,
      weekend_hour_rate: salary.weekend_hour_rate || 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (salaryId) => {
    try {
      const regularPay =
        parseFloat(editForm.regular_hours || 0) *
        parseFloat(editForm.hour_rate || 0);
      const weekendPay =
        parseFloat(editForm.weekend_hours || 0) *
        parseFloat(editForm.weekend_hour_rate || 0);
      const newSalary = regularPay + weekendPay;

      const updateData = {
        regular_hours: parseFloat(editForm.regular_hours),
        weekend_hours: parseFloat(editForm.weekend_hours),
        hour_rate: parseFloat(editForm.hour_rate),
        weekend_hour_rate: parseFloat(editForm.weekend_hour_rate),
        salary: newSalary,
        total_hours:
          parseFloat(editForm.regular_hours) +
          parseFloat(editForm.weekend_hours),
      };

      const response = await apiService.updateCompanySalary(
        salaryId,
        updateData,
      );

      if (response.success) {
        await loadSalaries();
        await loadSummary();
        setEditingId(null);
        setEditForm({});
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to save edit:", error);
      alert("שגיאה בשמירת עדכון");
    }
  };

  const handleAddSalary = async () => {
    if (!newSalaryForm.employee_id) {
      alert("נא לבחור עובד");
      return;
    }

    if (
      !newSalaryForm.salary_amount ||
      parseFloat(newSalaryForm.salary_amount) <= 0
    ) {
      alert("נא להזין סכום משכורת");
      return;
    }

    try {
      const employee = employees.find(
        (e) => e.employee_id === parseInt(newSalaryForm.employee_id),
      );
      if (!employee) {
        alert("עובד לא נמצא");
        return;
      }

      const regularHours = parseFloat(newSalaryForm.regular_hours || 0);
      const weekendHours = parseFloat(newSalaryForm.weekend_hours || 0);
      const totalHours = regularHours + weekendHours;
      const salaryAmount = parseFloat(newSalaryForm.salary_amount);

      const salaryData = {
        employee_id: employee.employee_id,
        month: selectedMonth,
        regular_hours: regularHours,
        weekend_hours: weekendHours,
        total_hours: totalHours,
        hour_rate: employee.hour_rate || 0,
        weekend_hour_rate: employee.weekend_hour_rate || 0,
        salary: salaryAmount,
        extras: 0,
        total_to_pay: salaryAmount,
        paid: false,
        employee_type: employee.type,
      };

      const response = await apiService.createSalary(salaryData);

      if (response.success) {
        await loadSalaries();
        await loadSummary();
        setShowAddForm(false);
        setNewSalaryForm({
          employee_id: "",
          regular_hours: "",
          weekend_hours: "",
          salary_amount: "",
        });
      } else {
        alert(`שגיאה: ${response.error || "יצירה נכשלה"}`);
      }
    } catch (error) {
      console.error("Failed to add salary:", error);
      alert("שגיאה בהוספת משכורת");
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const getFilteredSalaries = () => {
    return salaries.filter((salary) => {
      const matchesName =
        searchName === "" ||
        (salary.employee_name || "")
          .toLowerCase()
          .includes(searchName.toLowerCase());

      const matchesCustomer =
        selectedCustomer === "" || salary.customer_name === selectedCustomer;

      return matchesName && matchesCustomer;
    });
  };

  const openExtrasModal = (salary) => {
    setSelectedSalary(salary);
    setExtrasModalOpen(true);
  };

  const closeExtrasModal = () => {
    setExtrasModalOpen(false);
    setSelectedSalary(null);
    loadSalaries();
    loadSummary();
  };

  const filteredSalaries = getFilteredSalaries();
  const bothSummary = summary
    ? {
        total: filteredSalaries.length,
        totalToPay: filteredSalaries.reduce(
          (sum, s) => sum + (parseFloat(s.to_pay) || 0),
          0,
        ),
        paid: filteredSalaries.filter((s) => s.paid).length,
        paidAmount: filteredSalaries
          .filter((s) => s.paid)
          .reduce((sum, s) => sum + (parseFloat(s.to_pay) || 0), 0),
      }
    : null;

  return (
    <div className='salaries-container company-salaries'>
      <div className='salaries-header'>
        <div>
          <h1>משכורות קבלן וחברה</h1>
          <p>ניהול משכורות עובדים מסוג קבלן ו חברה</p>
        </div>
        <div className='header-actions'>
          <div className='month-selector'>
            <Calendar size={18} />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value=''>בחר חודש</option>
              {availableMonths.map((month) => (
                <option key={month} value={month}>
                  {formatMonth(month)}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className='add-salary-btn'
            title='הוסף משכורת'
          >
            <Plus size={18} />
            הוסף משכורת
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className='add-salary-form'>
          <div className='form-header'>
            <h3>הוסף משכורת חדשה</h3>
            <button onClick={() => setShowAddForm(false)} className='close-btn'>
              <X size={18} />
            </button>
          </div>
          <div className='form-content'>
            <div className='form-group'>
              <label>
                עובד קבלן וחברה <span className='required'>*</span>
              </label>
              <select
                value={newSalaryForm.employee_id}
                onChange={(e) =>
                  setNewSalaryForm({
                    ...newSalaryForm,
                    employee_id: e.target.value,
                  })
                }
                required
              >
                <option value=''>בחר עובד</option>
                {employees
                  .filter((emp) => {
                    const type = emp.type || emp.employee_type || "";
                    return type === "קבלן ו חברה";
                  })
                  .map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.employee_name} - {emp.customer_name} (
                      {emp.type || emp.employee_type})
                    </option>
                  ))}
              </select>
              <small
                style={{
                  color: "#64748b",
                  fontSize: "0.8rem",
                  marginTop: "0.25rem",
                }}
              >
                חודש:{" "}
                {selectedMonth
                  ? new Date(selectedMonth + "-01").toLocaleDateString(
                      "he-IL",
                      { year: "numeric", month: "long" },
                    )
                  : ""}
              </small>
            </div>
            <div className='form-row'>
              <div className='form-group'>
                <label>שעות רגילות</label>
                <input
                  type='number'
                  step='0.01'
                  min='0'
                  placeholder='0.00'
                  value={newSalaryForm.regular_hours}
                  onChange={(e) =>
                    setNewSalaryForm({
                      ...newSalaryForm,
                      regular_hours: e.target.value,
                    })
                  }
                />
              </div>
              <div className='form-group'>
                <label>שעות סופ״ש</label>
                <input
                  type='number'
                  step='0.01'
                  min='0'
                  placeholder='0.00'
                  value={newSalaryForm.weekend_hours}
                  onChange={(e) =>
                    setNewSalaryForm({
                      ...newSalaryForm,
                      weekend_hours: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className='form-group'>
              <label>
                סכום משכורת <span className='required'>*</span>
              </label>
              <input
                type='number'
                step='0.01'
                min='0'
                placeholder='0.00'
                value={newSalaryForm.salary_amount}
                onChange={(e) =>
                  setNewSalaryForm({
                    ...newSalaryForm,
                    salary_amount: e.target.value,
                  })
                }
                required
              />
            </div>
            <div className='form-actions'>
              <button onClick={handleAddSalary} className='save-btn'>
                <Save size={18} />
                שמור משכורת
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className='cancel-btn'
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {bothSummary && (
        <div className='summary-cards'>
          <div className='summary-card'>
            <div
              className='summary-icon'
              style={{ background: "#dbeafe", color: "#3b82f6" }}
            >
              <Users size={24} />
            </div>
            <div className='summary-content'>
              <p className='summary-label'>סה״כ </p>
              <h3 className='summary-value'>{bothSummary.total}</h3>
            </div>
          </div>

          {(user?.permission === "admin" || user?.permission === "owner") && (
            <>
              <div className='summary-card'>
                <div
                  className='summary-icon'
                  style={{ background: "#dcfce7", color: "#10b981" }}
                >
                  <DollarSign size={24} />
                </div>
                <div className='summary-content'>
                  <p className='summary-label'>סה״כ לתשלום</p>
                  <h3 className='summary-value'>
                    ₪
                    {bothSummary.totalToPay.toLocaleString("he-IL", {
                      maximumFractionDigits: 0,
                    })}
                  </h3>
                </div>
              </div>
              <div className='summary-card paid'>
                <div
                  className='summary-icon'
                  style={{ background: "#fce7f3", color: "#ec4899" }}
                >
                  <DollarSign size={24} />
                </div>
                <div className='summary-content'>
                  <p className='summary-label'>שולם</p>
                  <h3 className='summary-value'>
                    ₪
                    {bothSummary.paidAmount.toLocaleString("he-IL", {
                      maximumFractionDigits: 0,
                    })}
                  </h3>
                  <p className='summary-sublabel'>{bothSummary.paid} עובדים</p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {!loading && salaries.length > 0 && (
        <div className='filters-section'>
          <div className='search-box'>
            <Search size={18} />
            <input
              type='text'
              placeholder='חיפוש לפי שם עובד...'
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>
          <div className='customer-filter'>
            <Filter size={18} />
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              <option value=''>כל הלקוחות</option>
              {customers.map((customer) => (
                <option key={customer} value={customer}>
                  {customer}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className='loading-state'>
          <div className='spinner'></div>
          <p>טוען נתונים...</p>
        </div>
      ) : filteredSalaries.length === 0 ? (
        <div className='empty-state'>
          <UserPlus size={48} />
          <h3>אין משכורות להצגה</h3>
          <p>לא נמצאו משכורות עבור עובדי קבלן וחברה בחודש שנבחר</p>
          <button
            onClick={() => setShowAddForm(true)}
            className='add-salary-btn'
          >
            <Plus size={18} />
            הוסף משכורת ראשונה
          </button>
        </div>
      ) : (
        <div className='salaries-table-container'>
          <table className='salaries-table'>
            <thead>
              <tr>
                <th>שם עובד</th>
                <th style={{ maxWidth: "100px" }}>לקוח / אתר</th>
                <th>סה״כ שעות</th>
                <th>שעות קבלן</th>
                <th>שעות חברה</th>
                <th>תשלום קבלן</th>
                <th>תשלום חברה</th>
                <th>סה״כ לתשלום</th>
                <th>שולם קבלן</th>
                <th>שולם חברה</th>
              </tr>
            </thead>
            <tbody>
              {filteredSalaries.map((salary) => (
                <tr key={salary.id}>
                  {editingId === salary.id ? (
                    <>
                      <td>{salary.employee_name}</td>
                      <td
                        className='customer-site-cell'
                        style={{ maxWidth: "100px" }}
                      >
                        <div className='customer-name'>
                          {salary.customer_name || "-"}
                        </div>
                        <div className='site-name'>
                          {salary.site_name || "-"}
                        </div>
                      </td>
                      <td>
                        {(
                          (salary.total_regular_hours || 0) +
                          (salary.total_weekend_hours || 0)
                        ).toFixed(2)}
                      </td>
                      <td>{(salary.contractor_hours || 0).toFixed(2)}</td>
                      <td>{(salary.company_hours || 0).toFixed(2)}</td>
                      <td>₪{(salary.kablan_pay || 0).toFixed(2)}</td>
                      <td>₪{(salary.company_pay || 0).toFixed(2)}</td>
                      <td>₪{(salary.to_pay || 0).toFixed(2)}</td>
                      <td>
                        <input type='checkbox' checked={salary.paid} disabled />
                      </td>
                      <td>
                        <input type='checkbox' checked={salary.paid} disabled />
                      </td>
                    </>
                  ) : (
                    <>
                      <td
                        className='employee-name-cell clickable-cell'
                        onClick={() => openEmployeeModal(salary)}
                        title='לחץ לצפייה בפרטי עובד'
                      >
                        {salary.employee_name}
                      </td>
                      <td
                        className='customer-site-cell'
                        style={{ maxWidth: "100px" }}
                      >
                        <div className='customer-name'>
                          {salary.customer_name || "-"}
                        </div>
                        <div className='site-name'>
                          {salary.site_name || "-"}
                        </div>
                      </td>
                      <td>
                        {(
                          (salary.total_regular_hours || 0) +
                          (salary.total_weekend_hours || 0)
                        ).toFixed(2)}
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          value={
                            editingHours[salary.id]?.contractor !== undefined
                              ? editingHours[salary.id].contractor
                              : (salary.contractor_hours || 0).toFixed(2)
                          }
                          onChange={(e) =>
                            handleContractorHoursChange(salary, e.target.value)
                          }
                          onBlur={() => {
                            if (editingHours[salary.id]) {
                              handleSaveHoursSplit(salary);
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") e.target.blur();
                          }}
                          className='hours-input'
                          style={{
                            width: "70px",
                            textAlign: "center",
                            MozAppearance: "textfield",
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          value={
                            editingHours[salary.id]?.company !== undefined
                              ? editingHours[salary.id].company
                              : (salary.company_hours || 0).toFixed(2)
                          }
                          onChange={(e) =>
                            handleCompanyHoursChange(salary, e.target.value)
                          }
                          onBlur={() => {
                            if (editingHours[salary.id]) {
                              handleSaveHoursSplit(salary);
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") e.target.blur();
                          }}
                          className='hours-input'
                          style={{
                            width: "70px",
                            textAlign: "center",
                            MozAppearance: "textfield",
                          }}
                        />
                      </td>
                      <td>
                        <div className='editable-cell'>
                          <span>₪</span>
                          <input
                            type='number'
                            step='0.01'
                            value={
                              editingPayKablan[salary.id] !== undefined
                                ? editingPayKablan[salary.id]
                                : (salary.kablan_pay || 0).toFixed(2)
                            }
                            onChange={(e) =>
                              setEditingPayKablan((prev) => ({
                                ...prev,
                                [salary.id]: e.target.value,
                              }))
                            }
                            onBlur={() => {
                              if (editingPayKablan[salary.id] !== undefined) {
                                handleUpdateKablanPay(salary);
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") e.target.blur();
                            }}
                            className='to-pay-input'
                            style={{ width: "80px" }}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='editable-cell'>
                          <span>₪</span>
                          <input
                            type='number'
                            step='0.01'
                            value={
                              editingPayCompany[salary.id] !== undefined
                                ? editingPayCompany[salary.id]
                                : (salary.company_pay || 0).toFixed(2)
                            }
                            onChange={(e) =>
                              setEditingPayCompany((prev) => ({
                                ...prev,
                                [salary.id]: e.target.value,
                              }))
                            }
                            onBlur={() => {
                              if (editingPayCompany[salary.id] !== undefined) {
                                handleUpdateCompanyPay(salary);
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") e.target.blur();
                            }}
                            className='to-pay-input'
                            style={{ width: "80px" }}
                          />
                        </div>
                      </td>
                      <td className='total-cell'>
                        <span>
                          ₪
                          {(
                            (salary.kablan_pay || 0) + (salary.company_pay || 0)
                          ).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <input
                          type='checkbox'
                          checked={salary.paid_kablan || false}
                          onChange={(e) =>
                            handleUpdateSalary(
                              salary.id,
                              "paid_kablan",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type='checkbox'
                          checked={salary.paid_company || false}
                          onChange={(e) =>
                            handleUpdateSalary(
                              salary.id,
                              "paid_company",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {extrasModalOpen && selectedSalary && (
        <ExtrasModal
          isOpen={extrasModalOpen}
          onClose={closeExtrasModal}
          salary={selectedSalary}
          onUpdate={closeExtrasModal}
        />
      )}

      {/* Employee Edit Modal */}
      {employeeModalOpen && selectedEmployee && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={(reload) => {
            setEmployeeModalOpen(false);
            setSelectedEmployee(null);
            if (reload) {
              loadSalaries();
              loadSummary();
            }
          }}
        />
      )}
    </div>
  );
};

export default BothSalaries;
